import ReactSelect, {
  components as RSComponents,
  OptionProps,
} from "react-select";
import cn from "classnames";
import { Label, Text } from "components";
import { useTranslation } from "react-i18next";

export type RSOption = { label: string; value: string };

export type RSSelectFieldProps<T = string> = {
  id: string;
  name?: string;
  label?: string;
  description?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string | boolean;
  isPreview?: boolean;
  isClearable?: boolean;
  placeholder?: string;
  options: RSOption[];
  value: string | undefined;
  onChange: (value: T) => void;
};

const Option = (props: OptionProps<any, false>) => {
  const { isSelected, isFocused } = props;
  return (
    <RSComponents.Option {...props} className={undefined}>
      <div
        className={cn(
          "flex items-center justify-between px-3 py-2",
          "-bg--theme-colors-surface-neutral-level02 -text--component-colors-field-default-input-text",
          "hover:-bg--theme-colors-surface-neutral-level03 hover:-text--component-colors-field-hover-input-text",
          {
            "-bg--theme-colors-surface-neutral-level03 -text--component-colors-field-hover-input-text":
              isSelected || isFocused,
          },
        )}
      >
        <span>{(props.data as any).label}</span>
        <span className="ms-3">{isSelected ? "\u2713" : null}</span>
      </div>
    </RSComponents.Option>
  );
};

const RSSelectField = <T = string,>({
  id,
  name,
  label,
  description,
  required,
  disabled,
  error,
  isPreview,
  isClearable,
  placeholder,
  options,
  value,
  onChange,
}: RSSelectFieldProps<T>) => {
  const selected = value
    ? options.find((o) => o.value === value) || null
    : null;
  const { t } = useTranslation();

  return (
    <div className="w-full">
      {label && (
        <Label
          htmlFor={id}
          error={!!error}
          required={required}
          isPreview={isPreview}
        >
          {label}
        </Label>
      )}
      {isPreview ? (
        <Text className="-text--theme-colors-text-body text-xl">
          {options.find((o) => o.value === value)?.label || value || "-"}
        </Text>
      ) : (
        <ReactSelect
          inputId={id}
          name={name || id}
          isSearchable
          isDisabled={disabled}
          isClearable={isClearable}
          classNamePrefix="rs"
          unstyled
          placeholder={placeholder || t("common.labels.select")}
          options={options}
          value={selected}
          onChange={(opt) => {
            const selectedValue = ((opt as { value?: T } | null)?.value ??
              "") as unknown as T;
            onChange(selectedValue);
          }}
          classNames={{
            container: () => "w-full mt-2.5",
            control: (state) =>
              cn(
                "!min-h-[48px] !bg-inherit !border appearance-none",
                "!outline-none focus:!outline-none !shadow-none focus:!shadow-none focus:!ring-0",
                "-rounded-input",
                {
                  "-border--component-colors-field-error-input-border -text--component-colors-field-error-input-text":
                    !!error,
                  "-border--component-colors-field-selected-input-border":
                    !error && state.isFocused,
                  "-border--component-colors-field-default-input-border":
                    !error && !state.isFocused,
                },
              ),
            valueContainer: () => "!py-2.5 !px-3",
            singleValue: () =>
              "-text--component-colors-field-filled-input-text",
            input: () => "-text--component-colors-field-filled-input-text",
            placeholder: () =>
              "-text--component-colors-field-default-input-placeholder",
            menu: () =>
              "-bg--theme-colors-surface-neutral-level02 !z-50 border -border--component-colors-field-default-input-border",
            menuList: () => "py-0 max-h-64 overflow-auto",
            option: () => "px-0 py-0",
            indicatorsContainer: () => "pe-2",
            dropdownIndicator: () =>
              "-text--component-colors-field-default-input-text me-1.5",
            indicatorSeparator: () => "!hidden",
          }}
          components={{ Option }}
        />
      )}
      {description && (
        <Text className="text-sm -text--component-colors-field-default-description mt-1 mb-2">
          {description}
        </Text>
      )}
    </div>
  );
};

export default RSSelectField;
